import { AfterViewInit, Component, ElementRef, HostListener, OnInit, ViewChild } from '@angular/core';
import { NoteService } from '../../services/note.service';
import { NavigationHelper } from '../../helpers/navigation';
import { animate, style, transition, trigger } from "@angular/animations";
import { SecretionCard } from '../../../../src/app/interfaces/secretion-card.interface';
import { PottyNotesGestureController } from '../../utils/gesture.controller';
import { Router } from '@angular/router';

@Component({
    selector: 'describe-secretion-container',
    templateUrl: 'describe-secretion.html',
    styleUrls: ['describe-secretion.scss'],
    animations: [
        trigger('cardTransition', [
            transition(':enter', [
                style({ opacity: 0 }),
                animate('300ms ease-out', style({ opacity: 1 }))
            ]),
        ])
    ],
    standalone: false
})
export class DescribeSecretionContainer implements OnInit, AfterViewInit {
	public static readonly ComponentIdentifier: number = 3;
	public nextButtonText: string;
	public selectedHero = '';
	public selectedImagePath = '';
	public isCardsTextVisible = false;
	public isStartOfFlow: boolean;

	private initialTouchX: number | null = null;
	private initialTouchY: number | null = null;
	private touchStartTime: number | null = null;

	// How sensitive the auto scroll is.
	private scrollFactor: number = 125;

	@ViewChild('cardsContainer', { static: false }) cardsRef: ElementRef;

	public peeCards: SecretionCard[] = [
		{
			imgPath: 'assets/images/describe-secretion/pee-1.svg',
			imgText: 'Wow I am hydrated'
		},
		{
			imgPath: 'assets/images/describe-secretion/pee-2.svg',
			imgText: 'Yellow is happy color'
		},
		{
			imgPath: 'assets/images/describe-secretion/pee-3.svg',
			imgText: 'I need to drink more water'
		}
	]

	public pooCards: SecretionCard[] = [
		{
			imgPath: 'assets/images/describe-secretion/poo-1.svg',
			imgText: 'Basically constipated'
		},
		{
			imgPath: 'assets/images/describe-secretion/poo-2.svg',
			imgText: 'Did someone eat playdough?'
		},
		{
			imgPath: 'assets/images/describe-secretion/poo-3.svg',
			imgText: 'Sausages'
		},
		{
			imgPath: 'assets/images/describe-secretion/poo-4.svg',
			imgText: 'Snakes in a toilet'
		},
		{
			imgPath: 'assets/images/describe-secretion/poo-5.svg',
			imgText: 'It broke apart'
		},
		{
			imgPath: 'assets/images/describe-secretion/poo-6.svg',
			imgText: 'Fluffy and light'
		},
		{
			imgPath: 'assets/images/describe-secretion/poo-7.svg',
			imgText: 'Explosive'
		}
	]

	constructor(private noteService: NoteService,
		private navigationHelper: NavigationHelper,
		private router: Router) { }

	ngAfterViewInit(): void {
		setTimeout(() => {
			if (this.cardsRef?.nativeElement && this.noteService.selectedSecretionImg != null) {
				this.accountForScroll();
			}
		  }, 200);
	}

	ngOnInit(): void {
		this.isStartOfFlow = this.navigationHelper.isStartOfNoteFlow();
		this.nextButtonText = this.navigationHelper.isEndOfNoteFlow() ? 'Submit Note' : 'skip';

		this.selectedHero = this.noteService.selectedHero == null ? '' : this.noteService.selectedHero;

		if (this.noteService.selectedSecretionImg != null) {
			this.nextButtonText = 'next';
			this.selectedImagePath = this.noteService.selectedSecretionImg;
		}
	}

	public setSelectedHero(hero: string): void {
		if (this.selectedHero === hero) {
			return;
		}

		this.selectedImagePath = '';
		this.selectedHero = hero;
		this.noteService.selectedHero = hero;

		this.cardsRef?.nativeElement.scrollTo(0, 0);
	}

	public setSelectedImagePath(path: string): void {
		this.nextButtonText = 'next';
		this.selectedImagePath = path;
		this.noteService.selectedSecretionImg = path;
	}

	public saveSecretionMetadata(event:Event): void {
		const wasSkipped = this.noteService.selectedSecretionImg == null || this.navigationHelper.skipButtonPressed(event);
		this.noteService.setSecretionType(this.selectedImagePath, wasSkipped);
	}

	// Gesture control 
	@HostListener('touchstart', ['$event'])
	public onTouchStart(event: TouchEvent): void {
		if (this.ignoreArea(event)) return;
		this.initialTouchX = event.touches[0].clientX;
		this.initialTouchY = event.touches[0].clientY;
		this.touchStartTime = Date.now();
	}

	@HostListener('touchmove', ['$event'])
	public onTouchMove(event: TouchEvent): void {
		if (this.ignoreArea(event)) return;

		if (this.initialTouchX === null || this.touchStartTime === null || this.initialTouchY == null) return;

		const currentTouchX = event.touches[0].clientX;
		const currentTouchY = event.touches[0].clientY;
		const deltaX = currentTouchX - this.initialTouchX;
		const deltaY = currentTouchY - this.initialTouchY;
		const timeDiff = Date.now() - this.touchStartTime

		// This represents moving backward in the add notes flow.
		if (PottyNotesGestureController.MovedRightEnough(timeDiff, deltaX, deltaY)) {
			event.preventDefault();
			this.saveSecretionMetadata(event);
			this.router.navigate([`${this.navigationHelper.getPreviousComponentUrl()}`]);
		}
		// Otherwise, forward.
		else if (PottyNotesGestureController.MovedLeftEnough(timeDiff, deltaX, deltaY)) {
			this.saveSecretionMetadata(event);
			event.preventDefault();
			this.router.navigate([`${this.navigationHelper.getNextComponentUrl()}`]);
		}
	}

	@HostListener('touchend', ['$event'])
	public onTouchEnd(event: TouchEvent): void {
		event.stopPropagation();
	}

	private ignoreArea(event: TouchEvent) {
		// This represents the gestures being allowed to process when the cards are not showing. 
		if (this.cardsRef === undefined) return false;

		const rect = this.cardsRef.nativeElement.getBoundingClientRect();

		const touch = event.touches[0];
		return (
			touch.clientX >= rect.left &&
			touch.clientX <= rect.right &&
			touch.clientY >= rect.top &&
			touch.clientY <= rect.bottom
		  )
	}

	private accountForScroll() {
		const peeIndex = this.peeCards.findIndex(card => card.imgPath === this.noteService.selectedSecretionImg);

		// The first two options are displayed by default and do not need additional scrolling.
		if (peeIndex == 0 || peeIndex == 1) return;

		if (peeIndex != -1) {
			this.cardsRef.nativeElement.scrollLeft = peeIndex * this.scrollFactor;
			return;
		}

		// The first two options are displayed by default and do not need additional scrolling.
		const pooIndex = this.pooCards.findIndex(card => card.imgPath === this.noteService.selectedSecretionImg);
		if (pooIndex == 0 || pooIndex == 1) return;

		if (pooIndex !== -1) {
			this.cardsRef.nativeElement.scrollLeft = pooIndex * this.scrollFactor;
			return;
		}
	}
}
